import IParamTypeDescriptor from "../database/interfaces/paramScript/IParamTypeDescriptor";
import TParameters from "./TParameters";

/**
 * ADAT-VEZÉRELT mellékhatások: ha egy paraméter megváltozik, EGY MÁSIK paraméter (vagy annak egy mezője)
 * automatikusan a megadott értékre áll. A szabályok a descriptor `effects` tömbjében élnek, tehát a
 * kódban nincs paraméter-név vagy érték beégetve.
 *
 * Aktuális felhasználás (konyhai alsószekrény): a keresztmerevítő és a "Tető kapcsolat" össze van
 * kötve — merevítés bekapcsolása → tető Közéülő; tető Közéülőre → merevítés bekapcsol; tető Ráfekvőre →
 * merevítés kikapcsol.
 *
 * A mellékhatások NEM kaszkádolnak: a cél-paraméterbe írt érték már nem futtatja a CÉL saját effects-ét.
 * Így körkörös lánc nem alakulhat ki (A→B és B→A szabály egymás mellett is biztonságos), és a
 * felhasználói szándék egyetlen, kiszámítható lépésben érvényesül.
 */
export type TParamEffect = NonNullable<IParamTypeDescriptor["effects"]>[number];

/** A ParametersProcessor a több példányban szereplő paramétereket `Név_1`, `Név_2`… néven tárolja. */
const baseName = (name: string): string => name.replace(/_\d+$/, "");

/** Egy érték adott mezője (composite értéknél), egyébként maga az érték — stringként. */
const readField = (value: any, field?: string): string =>
    (field && value && typeof value === "object") ? String(value[field] ?? "") : String(value ?? "");

/** Teljesül-e a szabály feltétele az ÚJ értékre? */
const conditionHolds = (effect: TParamEffect, newValue: any): boolean => {
    const current = readField(newValue, effect.when?.field);
    if (effect.when?.in && !effect.when.in.includes(current)) return false;
    if (effect.when?.notIn && effect.when.notIn.includes(current)) return false;
    return true;
};

/** A cél paraméter VÉGLEGES neve a `parameters` állapotban (bázisnév szerint keresve). */
const findTargetName = (parameters: TParameters, paramName: string): string | undefined =>
    Object.keys(parameters || {}).find((name) => baseName(name) === paramName);

/**
 * A megváltozott paraméter azon szabályai, amelyek feltétele teljesül az ÚJ értékre. Szándékosan NEM
 * néz a paraméter-állapotba: így a hívó a state-frissítésen KÍVÜL kiszámíthatja (pl. a mentés-értesítéshez),
 * miközben a tényleges írás a friss state-en, az `applyEffect`-tel történik.
 */
export const matchingEffects = (
    effects: TParamEffect[] | undefined,
    newValue: any
): TParamEffect[] =>
    (effects ?? []).filter((effect) =>
        !!effect?.set?.paramName && !!effect.set.property && conditionHolds(effect, newValue)
    );

/**
 * Egy szabály alkalmazása egy paraméter-állapotra. TISZTA függvény: ha nincs mit írni (nincs ilyen
 * cél-paraméter, a cél már a kívánt értéken áll, vagy a mostani értéke a `keepIfIn` listában van), akkor
 * VÁLTOZATLANUL az eredeti objektumot adja vissza — így a hívó `prev === next` összehasonlítása
 * megmarad, és nem indul felesleges Unity-kör.
 *
 * Azért kap teljes állapotot (és nem előre kiszámolt írást), mert composite célnál a MEGLÉVŐ objektumot
 * egészíti ki: a merge-alapnak a FRISS state-ből kell jönnie, különben egy párhuzamos változás
 * visszaíródhatna.
 */
export const applyEffect = (
    parameters: TParameters,
    effect: TParamEffect,
    resolvePropertyKey: (property: string) => string
): TParameters => {
    const targetName = findTargetName(parameters, effect.set.paramName);
    if (!targetName) return parameters;

    const targetKey = resolvePropertyKey(effect.set.property);
    const targetCurrent = parameters[targetName]?.[targetKey];
    const currentField = readField(targetCurrent, effect.set.field);

    if (currentField === effect.set.value) return parameters;
    if (effect.set.keepIfIn?.includes(currentField)) return parameters;

    const value = effect.set.field
        ? { ...(targetCurrent && typeof targetCurrent === "object" ? targetCurrent : {}),
            [effect.set.field]: effect.set.value }
        : effect.set.value;

    return { ...parameters, [targetName]: { ...parameters[targetName], [targetKey]: value } };
};
